import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { EyebrowLabel } from "@/ui/molecules/EyebrowLabel";
import type { ShiftOptionWindowGroup } from "@/utils/shiftOptionWindows";
import { EditTimeTouchWindowGroup } from "./EditTimeTouchWindowGroup";

type Props = {
	/** The department's full-shift windows, grouped by their length in hours (`groupShiftOptionWindowsByLength`). */
	groups: readonly ShiftOptionWindowGroup[];
	selectedStart: DateTime;
	selectedEnd: DateTime;
	onPick: (start: DateTime, end: DateTime) => void;
};

/**
 * `EditTimePopover`'s full-shift windows for a finger (`appearance="touch"`): each length is an upright label over a
 * row of touch-sized chips, instead of a rotated "12H" beside outlined desktop buttons. The window the editor holds
 * right now is marked — compared by value, so the shift's own hours read as chosen the moment the editor opens.
 *
 * The section heading is the sheet's own eyebrow (`EyebrowLabel`), not the desktop's faded heading.
 */
export const EditTimeTouchWindows: FC<Props> = ({ groups, selectedStart, selectedEnd, onPick }) => {
	const { t } = useTranslation();
	return (
		<Box component="section" sx={{ mb: 2.5 }}>
			<EyebrowLabel component="h2" sx={{ m: 0 }}>
				{t("choose.shift")}
			</EyebrowLabel>
			{groups.map(({ lengthBucketHours, windows }) => (
				<EditTimeTouchWindowGroup
					key={lengthBucketHours}
					length={String(lengthBucketHours)}
					windows={windows.map(({ start, end }): [DateTime, DateTime] => [start, end])}
					selectedStart={selectedStart}
					selectedEnd={selectedEnd}
					onPick={onPick}
				/>
			))}
		</Box>
	);
};
